import { useMemo } from 'react';
import type { OtherSaleProduct } from '../../types/otherSales';
import type { Store } from '../../types/stores';
import { formatMoney } from '../../utils/costFormatting';
import { cartQuantityFor, formatProductTaxLabel, type CartLine, type PosCatalogState } from '../../utils/otherSalePos';
import { POS_CATALOG_VIEWS, filterProducts, type PosCatalogView } from '../../utils/posView';
import { Icon } from '../ui/Icon';
import { Button, EmptyPanel, ErrorPanel, Field, IconButton, LoadingPanel, Select, Skeleton } from '../ui/primitives';
import { cx } from '../ui/primitives/cx';

// Rediseño · Bloque D — la pantalla de venta (P-025): encabezado, buscador y catálogo. Solo dibuja.
//
// Lo que la pantalla aprobada trae y aquí NO está, porque el producto no lo tiene: la foto de cada
// producto, las categorías y «Favoritos», «Caja 1» y «Turno activo» (B0-14). El dibujo del negocio
// de la maqueta es de un negocio concreto; P-081 quitó también el icono que lo sustituía (ver PosHeader).

/** Otra vista de la misma sección (Historial, Catálogo de productos…), a un toque del punto de venta. */
export interface PosViewLink {
  id: string;
  label: string;
  onOpen: () => void;
}

/**
 * Quién vende, dónde, y a dónde más se puede ir.
 *
 * La tienda es la del flujo: la que esta persona tiene elegida para vender. Quien tiene varias la
 * cambia aquí mismo; quien tiene una —o es un empleado, que vende siempre en la suya— la lee.
 *
 * P-081 (FI-121): vender es lo principal, así que el contexto ocupa menos alto. El negocio va en un
 * renglón, sin el icono de adorno; quién vende ya lo dice la barra de la app (no se repite aquí); y
 * «Tienda» va al lado de su selector, no encima.
 */
export function PosHeader({ businessName, storeName, stores, storeId, storeLocked, disabled, onStoreSelect, links }: {
  businessName: string;
  storeName: string;
  stores: Store[];
  storeId: string;
  /** Un empleado no elige tienda: el servidor le pone la suya. */
  storeLocked: boolean;
  disabled: boolean;
  onStoreSelect: (storeId: string) => void;
  links: PosViewLink[];
}) {
  const choosesStore = !storeLocked && stores.length > 1;
  return (
    <header className="n3-pos__head">
      <div className="n3-pos__head-main">
        <h1 className="n3-pos__head-title" id="pos-title">
          <span className="n3-visually-hidden">Vender · </span>
          {businessName}
        </h1>
        {/* Quien no elige tienda la lee aquí; quien elige, la ve en su selector. */}
        {!choosesStore && <p className="n3-pos__head-sub">{storeName}</p>}
      </div>
      {choosesStore && (
        <Field className="n3-pos__store" label="Tienda">
          <Select disabled={disabled} onChange={(event) => onStoreSelect(event.target.value)} value={storeId}>
            {stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}
          </Select>
        </Field>
      )}
      <PosViewLinks disabled={disabled} links={links} />
    </header>
  );
}

/**
 * Los enlaces a las otras vistas de la sección. Van también en los estados sin tienda: mientras se
 * vende la página no dibuja sus pestañas, y sin esto no habría cómo llegar al historial desde ahí.
 */
export function PosViewLinks({ links, disabled = false }: { links: PosViewLink[]; disabled?: boolean }) {
  if (links.length === 0) return null;
  return (
    <nav aria-label="Más de Ventas e ingresos" className="n3-pos__views">
      {/* P-080: mientras se guarda una venta no se sale del punto de venta: con «Cobrar» en la barra
          ya no hay una hoja que los tape, y salir a media apertura dejaría la venta sin pantalla. */}
      {links.map((link) => <Button className="n3-pos__view" disabled={disabled} key={link.id} onClick={link.onOpen} variant="tonal">{link.label}</Button>)}
    </nav>
  );
}

/**
 * El catálogo: tocar un producto lo agrega al pedido. La burbuja dice cuántos van; quitar se hace en
 * el pedido, no aquí.
 *
 * El buscador filtra lo que ya está en pantalla. «No encontramos ese producto» NO es «no hay
 * productos»: el catálogo vacío es otro estado, y solo se afirma cuando el servidor lo dijo.
 *
 * P-080: se ve en Tarjetas (la de siempre) o en Lista, una fila por producto para ver muchos de un
 * golpe. Lista dibuja nombre, precio y cuántos van; el IVA no se dibuja ahí (no hace falta para tocar y
 * vender) pero sigue en el nombre accesible, en el pedido y en el cobro. Las dos vistas agregan con el
 * mismo toque y el mismo nombre accesible; solo una está montada a la vez.
 */
export function PosCatalog({ state, error, products, cart, query, busy, canManageCatalog, view, onView, onQuery, onRetry, onGoToCatalog, onAdd }: {
  state: PosCatalogState;
  error: string | undefined;
  products: OtherSaleProduct[] | undefined;
  cart: CartLine[];
  query: string;
  busy: boolean;
  canManageCatalog: boolean;
  view: PosCatalogView;
  onView: (view: PosCatalogView) => void;
  onQuery: (query: string) => void;
  onRetry: () => void;
  onGoToCatalog: () => void;
  onAdd: (product: OtherSaleProduct) => void;
}) {
  const shown = useMemo(() => filterProducts(products ?? [], query), [products, query]);

  if (state === 'error') {
    return (
      <div className="n3-pos__catalog">
        <ErrorPanel description={error} onRetry={onRetry} title="No se pudieron cargar los productos" />
      </div>
    );
  }
  if (state === 'loading') {
    return (
      <div className="n3-pos__catalog">
        <LoadingPanel className="n3-pos__grid" label="Cargando productos…">
          {Array.from({ length: 6 }, (_, index) => <Skeleton className="n3-pos__product-skeleton" key={index} />)}
        </LoadingPanel>
      </div>
    );
  }
  if (state === 'empty') {
    return (
      <div className="n3-pos__catalog">
        <EmptyPanel
          action={canManageCatalog ? <Button onClick={onGoToCatalog} variant="secondary">Ir al catálogo</Button> : undefined}
          description={canManageCatalog ? 'Da de alta productos en el catálogo para poder venderlos aquí.' : 'Pide a un dueño o gerente que dé de alta productos en el catálogo.'}
          icon="sell"
          title="No hay productos disponibles para esta tienda"
        />
      </div>
    );
  }

  return (
    <div className="n3-pos__catalog">
      <div className="n3-pos__tools">
        <div className="n3-pos__search">
          <span aria-hidden="true" className="n3-pos__search-icon"><Icon name="search" size={20} /></span>
          <input
            aria-label="Buscar productos"
            autoComplete="off"
            className="n3-pos__search-input"
            enterKeyHint="search"
            onChange={(event) => onQuery(event.target.value)}
            placeholder="Buscar productos…"
            type="search"
            value={query}
          />
          {query !== '' && (
            <IconButton className="n3-pos__search-clear" label="Borrar búsqueda" onClick={() => onQuery('')} variant="soft">
              <Icon name="close" size={18} />
            </IconButton>
          )}
        </div>
        {/* Dos botones que dicen cuál está puesto (`aria-pressed`); tocar el puesto lo deja puesto. */}
        <div aria-label="Vista del catálogo" className="n3-pos__viewmode" role="group">
          {POS_CATALOG_VIEWS.map((option) => (
            <button
              aria-label={option.label}
              aria-pressed={view === option.value}
              className="n3-pos__viewmode-option"
              key={option.value}
              onClick={() => onView(option.value)}
              title={option.label}
              type="button"
            >
              <Icon name={option.value === 'list' ? 'list' : 'grid'} size={20} />
            </button>
          ))}
        </div>
      </div>

      {shown.length === 0 ? (
        <div className="n3-pos__no-results" role="status">
          <strong className="n3-pos__no-results-title">No encontramos ese producto</strong>
          <span>Prueba con otro nombre.</span>
        </div>
      ) : (
        <ul aria-label="Productos" className={view === 'list' ? 'n3-pos__list' : 'n3-pos__grid'}>
          {shown.map((product) => {
            const quantity = cartQuantityFor(cart, product.id);
            const price = formatMoney(product.price);
            const tax = formatProductTaxLabel(product);
            const label = `Agregar ${product.name}, ${price}, ${tax}${quantity > 0 ? `. En el pedido: ${quantity}` : ''}`;
            if (view === 'list') {
              return (
                <li className="n3-pos__list-item" key={product.id}>
                  <button aria-label={label} className={cx('n3-pos__row', quantity > 0 && 'n3-pos__row--in')} disabled={busy} onClick={() => onAdd(product)} type="button">
                    <span className="n3-pos__row-name">{product.name}</span>
                    {quantity > 0 && <span aria-hidden="true" className="n3-pos__row-qty">{quantity}</span>}
                    <span className="n3-pos__row-price">{price}</span>
                  </button>
                </li>
              );
            }
            return (
              <li className="n3-pos__grid-item" key={product.id}>
                <button
                  aria-label={label}
                  className={cx('n3-pos__product', quantity > 0 && 'n3-pos__product--in')}
                  disabled={busy}
                  onClick={() => onAdd(product)}
                  type="button"
                >
                  <span className="n3-pos__product-name">{product.name}</span>
                  <span className="n3-pos__product-price">{price}</span>
                  <span className="n3-pos__product-tax">{tax}</span>
                  {quantity > 0 && <span aria-hidden="true" className="n3-pos__product-qty">{quantity}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
